import { getAPIUrl } from '@nextUtils/urlUtils';
import { AbTest, AbTests } from '@nextTypes/abTest';
import { mixpanelTrackNext } from '../common/mixpanel';

type AbTestAssignmentFromApi = {
  /**
   * The name of the test - typically a number,
   * but stored as a string.
   * It will look something like "442".
   */
  testID: string;
  /**
   * The unique identifier for the variant -
   * it will typically look something like "Control"
   */
  variant: string;
  /**
   * The date the user was assigned to this A/B test - i.e. "2023-10-01T12:00:00Z"
   */
  dateAssignedAt: string;
  /**
   * Whether this assignment is synced from the user model
   */
  isSyncedFromUserModel: boolean;
};

type GetAssignedTestsResponse = {
  activeABTestAssignments: {
    [testID: string]: AbTestAssignmentFromApi;
  };
};

/**
 * Fetches assigned A/B tests from the server API.
 *
 * The server is responsible for:
 * 1. Identifying the user (either by auth or visitor cookie)
 * 2. Retrieving any existing assignments
 * 3. Creating new assignments for tests the user hasn't seen
 * 4. Returning the complete set of active assignments
 *
 * This ensures consistent assignments across devices and sessions.
 *
 * @returns {Promise<GetAssignedTestsResponse>} The active A/B test assignments for the user.
 * Example response:
 * {
 *   activeABTestAssignments: {
 *     "442": {
 *       "testID": "442",
 *       "variant": "Control",
 *       "dateAssignedAt": "2023-10-01T12:00:00Z",
 *       "isSyncedFromUserModel": true
 *     }
 *   }
 * }
 */
export const fetchActiveAssignedTests =
  async (): Promise<GetAssignedTestsResponse> => {
    try {
      const response = await fetch(
        `${getAPIUrl()}/api/ab-tests/active-assignments`,
      );
      if (!response.ok) {
        throw new Error('Failed to fetch AB test assignments');
      }

      const { data } = await response.json();

      return {
        activeABTestAssignments: data.activeABTestAssignments || {},
      };
    } catch (error) {
      // Return empty data on error
      return {
        activeABTestAssignments: {},
      };
    }
  };

/**
 * Registers an impression for a specific A/B test variant.
 *
 * @param abTestName - The name of the A/B test.
 * @param variantName - The name of the variant.
 */
export const fetchRegisterImpression = async (
  abTestName: string,
  variantName: string,
): Promise<void> => {
  const response = await fetch(`${getAPIUrl()}/api/ab-tests/impression`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      abTestName,
      variantName,
    }),
  });

  mixpanelTrackNext('AB Test Impression', {
    testName: abTestName,
    variantName,
  });

  if (!response.ok) {
    throw new Error('Failed to register impression');
  }
};

/**
 * Registers a conversion for a specific A/B test variant.
 *
 * @param abTestName - The name of the A/B test.
 * @param variantName - The name of the variant.
 */
export const fetchRegisterConversion = async (
  abTestName: string,
  variantName: string,
): Promise<void> => {
  const response = await fetch(`${getAPIUrl()}/api/ab-tests/conversion`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      abTestName,
      variantName,
    }),
  });

  mixpanelTrackNext('AB Test Conversion', {
    testName: abTestName,
    variantName,
  });

  if (!response.ok) {
    throw new Error('Failed to register conversion');
  }
};

type SendAbTestAssignmentsResponse = {
  message: string;
  syncedTests: {
    [testID: string]: AbTestAssignmentFromApi;
  };
};

export const sendAbTestAssignmentsToServer = async (
  newlyAssignedTests: AbTests,
): Promise<SendAbTestAssignmentsResponse> => {
  const response = await fetch(
    `${getAPIUrl()}/api/ab-tests/sync-user-ab-tests`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ newlyAssignedTests }),
    },
  );

  if (!response.ok) {
    const { error } = await response.json();
    throw new Error(error?.message || 'Failed to sync A/B tests');
  }

  const { data } = await response.json();
  return {
    message: data.message,
    syncedTests: data.syncedTests,
  };
};

/**
 * Merge a new A/B assignment into the existing store.
 *
 * Principles:
 *  1) If there's no existing assignment, accept the new one.
 *  2) Assignments coming from the user model (isSyncedFromUserModel) always take precedence.
 *  3) Otherwise, “first assignment wins”: keep the one with the earlier dateAssignedAt.
 *
 * @param {AbTest | null} existingTest  – current assignment in store (or null)
 * @param {AbTestAssignmentFromApi} newAssignment  – incoming assignment from API
 * @returns {AbTest} the assignment to keep
 */
export const mergeAbTest = (
  existingTest: AbTest | null,
  newAssignment: AbTestAssignmentFromApi,
): AbTest => {
  // 1) No existing assignment → accept new assignment
  if (!existingTest) {
    return newAssignment;
  }

  // 2) New assignment from user model, but existing isn't → accept new assignment
  if (
    newAssignment.isSyncedFromUserModel === true &&
    existingTest.isSyncedFromUserModel === false
  ) {
    return newAssignment;
  }

  // 3) Existing from user model → keep it
  if (existingTest.isSyncedFromUserModel === true) {
    return existingTest;
  }

  // 4) Neither is from user model → first assignment wins (earlier date)
  return new Date(existingTest.dateAssignedAt) <=
    new Date(newAssignment.dateAssignedAt)
    ? existingTest
    : newAssignment;
};
